import { HTTP_METHODS } from '../constants/httpMethods';
import { apiService } from './ApiService';
import { ApiResponse } from '../interfaces/responses/ApiResponse';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { ITaxonomySector, ITaxonomyFamily, ITaxonomyType } from '../interfaces/heavyDutyMachinery/ITaxonomy';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

// i18next can report a region-qualified language ("es-ES", "en-US") --
// TaxonomyController only knows the bare "es"/"en" SectorTranslation/etc.
// rows, so this is the one place that narrows down to just the primary
// subtag before it ever reaches the network call or a cache key.
const normalizeLanguage = (language: string): string => (language || 'es').split('-')[0].toLowerCase();

// Effectively-static reference data (see TaxonomyController's own 24h
// IMemoryCache) fetched by many independent consumers on the same page
// (map filters, the 3 filter modals, every category picker) -- one
// in-memory promise per language shared across all of them instead of one
// GET per mounted component. Cleared on a failed fetch so a transient
// network error doesn't poison every future call for the rest of the
// session.
const taxonomyPromises = new Map<string, Promise<ITaxonomySector[]>>();

export const getTaxonomy = async (language: string): Promise<ITaxonomySector[]> => {
  const lang = normalizeLanguage(language);
  let promise = taxonomyPromises.get(lang);
  if (!promise) {
    promise = (async () => {
      const response = await apiService<ApiResponse<ITaxonomySector[]>>({
        url: API_ENDPOINTS.TAXONOMY,
        method: HTTP_METHODS.GET,
        params: { lang },
        baseUrl: heavyDutyMachineryBaseUrl,
      });
      if (response.success) return response.data.data;
      throw new Error(response.error || 'Failed to load taxonomy');
    })();
    promise.catch(() => { taxonomyPromises.delete(lang); });
    taxonomyPromises.set(lang, promise);
  }
  return promise;
};

/** Every leaf ITaxonomyType across every sector/family, flattened -- for consumers (search-as-you-type, plain lookups) that don't need the tree shape. */
export const flattenTaxonomyTypes = (sectors: ITaxonomySector[]): ITaxonomyType[] =>
  sectors.flatMap((sector) => sector.families.flatMap((family) => family.types));

/** ITaxonomyFamily -> its parent ITaxonomySector, for consumers that only have a family and need to group/label by sector too. */
export const flattenTaxonomyFamilies = (sectors: ITaxonomySector[]): { sector: ITaxonomySector; family: ITaxonomyFamily }[] =>
  sectors.flatMap((sector) => sector.families.map((family) => ({ sector, family })));

/** Looks up a single MachineryType by its Machinery.Category/QualifiedCategories/CompatibleCategories/MachineryCategory value (MachineryType.Code -- language-neutral, see ITaxonomy.ts's own doc comment). */
export const findTaxonomyTypeByCode = (sectors: ITaxonomySector[], code: string): ITaxonomyType | undefined =>
  flattenTaxonomyTypes(sectors).find((type) => type.code === code);
